import { TimelineItemsParams } from '@kudos/http-client'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded'
import { Selectable } from '@shared/filters/selections'
import { firstValue } from '@shared/search/state'
import { TFunction } from '@shared/types/types'
import { TimelineDirection } from '../types'

export const DEFAULT_TIMELINE_DIRECTION: TimelineDirection = TimelineDirection.RECEIVED

const RECOGNITION_ITEM_TYPE = 'recognition'
const AWARD_ITEM_TYPE = 'award'

/** The persisted filter holds a single-element array, or nothing at all yet. */
export const resolveTimelineDirection = (direction: string[] | undefined): TimelineDirection =>
  firstValue(direction) === TimelineDirection.GIVEN
    ? TimelineDirection.GIVEN
    : DEFAULT_TIMELINE_DIRECTION

const allowedItemTypes = (canBrowseRecognitions: boolean): string[] =>
  canBrowseRecognitions ? [RECOGNITION_ITEM_TYPE, AWARD_ITEM_TYPE] : [AWARD_ITEM_TYPE]

export const getMemberItemTypeFilterOptions = (
  canBrowseRecognitions: boolean,
  isMobile: boolean,
  t: TFunction,
) => {
  const recognitionSelection: Selectable = {
    id: RECOGNITION_ITEM_TYPE,
    name: t('Recognition'),
    startIcon: isMobile ? <StarOutlineRoundedIcon /> : undefined,
  }
  const awardSelection: Selectable = {
    id: AWARD_ITEM_TYPE,
    name: t('Award'),
    startIcon: isMobile ? <EmojiEventsOutlinedIcon /> : undefined,
  }
  return canBrowseRecognitions ? [recognitionSelection, awardSelection] : [awardSelection]
}

/**
 * Filters persist the direction rather than a member id, so a filter set on one
 * profile can never be applied to another member's timeline. The id is resolved
 * from the profile being viewed at query time.
 */
export const parseTimelineDirection = (
  direction: string[] | undefined,
  tenantMemberId: string | undefined,
): Pick<TimelineItemsParams, 'senderId' | 'recipientId'> =>
  resolveTimelineDirection(direction) === TimelineDirection.GIVEN
    ? { senderId: tenantMemberId }
    : { recipientId: tenantMemberId }

/**
 * The type filter is multi-select and persisted, so a selection made while
 * recognition browse was available can outlive the permission. Compare the
 * result against the input to detect a selection that needs cleaning up.
 */
export const allowedSelectedItemTypes = (
  itemTypeIn: string[] | undefined,
  canBrowseRecognitions: boolean,
): string[] => {
  const allowed = allowedItemTypes(canBrowseRecognitions)
  return itemTypeIn?.filter(itemType => allowed.includes(itemType)) ?? []
}

/** No explicit selection means every type the member is allowed to browse. */
export const parseTimelineItemTypes = (
  itemTypeIn: string[] | undefined,
  canBrowseRecognitions: boolean,
): string[] => {
  const selected = allowedSelectedItemTypes(itemTypeIn, canBrowseRecognitions)
  return selected.length ? selected : allowedItemTypes(canBrowseRecognitions)
}
